import { 
  Component, 
  OnInit, 
  input, 
  signal, 
  computed, 
  effect, 
  inject, 
  DestroyRef,
  ChangeDetectionStrategy 
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PostService } from '../services/post.service';

// Type definitions for better type safety
interface PostInfo {
  post_type: 'IMAGE' | 'VIDEO' | 'GIF' | 'TEXT' | 'SURVEY';
  url?: string;
  text?: string;
  thumbnail?: string;
}

interface Post {
  id: number;
  post_info: PostInfo;
  title?: string;
  created_at?: number;
  priority?: number;
}

interface PostsResponse {
  data: {
    posts: Post[];
  };
}

interface CompanyInfo {
  name: string;
  color: string;
}

@Component({
  selector: 'ibero-carrusel',
  standalone: true,
  imports: [RouterLink, CommonModule],
  templateUrl: './carrusel.html',
  styleUrl: './carrusel.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Carrusel implements OnInit {
  // Modern input signal (Angular 17.1+)
  public gsid = input.required<number>();

  // Dependency injection using inject() (Angular 14+)
  private readonly postService = inject(PostService);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management (Angular 16+)
  public companyInfo = signal<CompanyInfo>({
    name: '',
    color: '#000'
  });
  public posts = signal<Post[]>([]);
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);
  public currentSlideIndex = signal<number>(0);

  // Computed signals for derived state
  public hasPosts = computed(() => this.posts().length > 0);
  public displayPosts = computed(() => this.posts().slice(0, 5)); // Limit to 5 posts for carousel
  public isEmpty = computed(() => !this.isLoading() && !this.hasPosts() && !this.error());
  public companyName = computed(() => this.companyInfo().name);
  public companyColor = computed(() => this.companyInfo().color);

  constructor() {
    // Effect to load posts when gsid changes
    effect(() => {
      const gsidValue = this.gsid();
      if (gsidValue && gsidValue > 0) {
        this.loadHighPriorityPosts(gsidValue);
      }
    }, { allowSignalWrites: true });

    // Initialize company info from localStorage
    this.initializeCompanyInfo();
  }

  ngOnInit(): void {
    // Additional initialization if needed
    this.logComponentInit();
  }

  private initializeCompanyInfo(): void {
    try {
      const name = localStorage.getItem('gsname') || 'Sin nombre';
      const color = localStorage.getItem('gscolor') || '#d4af37';
      
      this.companyInfo.set({ name, color });
    } catch (error) {
      console.error('Error reading company info from localStorage:', error);
      this.companyInfo.set({ name: 'Sin nombre', color: '#d4af37' });
    }
  }

  private loadHighPriorityPosts(gsid: number): void {
    console.log('Loading high priority posts for GSID:', gsid);
    this.isLoading.set(true);
    this.error.set(null);

    this.postService.getPostsHighPriority(gsid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: PostsResponse) => {
          this.isLoading.set(false);
          
          if (response?.data?.posts && Array.isArray(response.data.posts)) {
            this.posts.set(response.data.posts);
            console.log(`Successfully loaded ${response.data.posts.length} high priority posts`);
          } else {
            console.warn('Invalid posts response structure:', response);
            this.posts.set([]);
          }
        },
        error: (error) => {
          console.error('Error loading high priority posts:', error);
          this.isLoading.set(false);
          this.error.set('Error al cargar el contenido destacado');
          this.posts.set([]);
        }
      });
  }

  public retryLoadPosts(): void {
    const gsidValue = this.gsid();
    if (gsidValue > 0) {
      this.loadHighPriorityPosts(gsidValue);
    }
  }

  public navigateToSlide(index: number): void {
    if (index >= 0 && index < this.posts().length) {
      this.currentSlideIndex.set(index);
      
      // Scroll to the slide
      const slideElement = document.getElementById(`slide-${index}`);
      if (slideElement) {
        slideElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }

  public onSlideChange(event: Event): void {
    // Detect which slide is currently visible
    const slider = event.target as HTMLElement;
    if (slider) {
      const slideWidth = slider.offsetWidth;
      const scrollLeft = slider.scrollLeft;
      const newIndex = Math.round(scrollLeft / slideWidth);
      
      if (newIndex !== this.currentSlideIndex()) {
        this.currentSlideIndex.set(newIndex);
      }
    }
  }

  public getPostTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      'IMAGE': 'Imagen',
      'VIDEO': 'Video',
      'GIF': 'GIF',
      'TEXT': 'Texto',
      'SURVEY': 'Encuesta'
    };
    return labels[type] || 'Contenido';
  }

  public clearError(): void {
    this.error.set(null);
  }

  private logComponentInit(): void {
    console.log('Carrusel component initialized with GSID:', this.gsid());
  }

  public trackByPostId(index: number, post: Post): number {
    return post.id;
  }
}
